import Alert from "@/common/components/Alert";
import SelectController from "@/common/components/SelectController";
import { useEquipmentCategories } from "@/common/hooks/queries/useEquipmentCategories";
import { useEquipmentManufacturers } from "@/common/hooks/queries/useEquipmentManufacturers";
import useDebounce from "@/common/hooks/useDebounce";
import { Mapper } from "@/common/services/mapper";
import { EquipmentCategoryData } from "@/features/equipmentModels/types/equipmentCategories";
import { MaintenanceSpecificationValidationSchema } from "@/features/establishments/schemas/maintenanceSpecification.schema";
import { useMaintenanceObjectiveSystems } from "@/features/maintenance/objectiveMaintenance/objectiveSystems/hooks/useMaintenanceObjectiveSystems";
import { MaintenanceObjectiveSystemData } from "@/features/maintenance/objectiveMaintenance/objectiveSystems/types/MaintenanceObjectiveSystem.type";
import { AlertCircleIcon } from "lucide-react";
import { useFormContext } from "react-hook-form";

interface MaintenanceSpecificationFormProps {
  alertText: {
    title: string;
    description: string;
  };
}

const MaintenanceSpecificationForm = ({ alertText }: MaintenanceSpecificationFormProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<MaintenanceSpecificationValidationSchema>();

  const [searchEquipmentCategories, setSearchEquipmentCategories] = useDebounce('');
  const [searchEquipmentManufacturers, setSearchEquipmentManufacturers] = useDebounce('');
  const [searchObjectiveSystems, setSearchObjectiveSystems] = useDebounce('');

  const {
    data: equipmentCategoriesOptions,
  } = useEquipmentCategories({
    filters: {
      search: searchEquipmentCategories,
    },
    mapper: Mapper.mapToOptions<EquipmentCategoryData>({
      valueFieldName: 'id',
      labelFieldName: 'display_name',
    }),
  });

  const {
    data: equipmentManufacturersOptions,
  } = useEquipmentManufacturers({
    filters: {
      search: searchEquipmentManufacturers,
    },
    mapper: Mapper.mapToOptions({
      valueFieldName: 'id',
      labelFieldName: 'display_name',
    }),
  });

  const {
    data: objetiveSystemsOptions,
  } = useMaintenanceObjectiveSystems({
    filters: {
      search: searchObjectiveSystems,
    },
    mapper: Mapper.mapToOptions<MaintenanceObjectiveSystemData>({
      valueFieldName: 'id',
      labelFieldName: 'display_name',
    }),
  });

  return (
    <div className="flex flex-col gap-4 text-white-800">
      <div className="flex items-center gap-3">
        <Alert
          variant="warning"
          Icon={AlertCircleIcon}
          containerClassName="bg-transparent p-0 flex-1"
        >
          <div className="flex flex-col">
            <p className="text-xs">
              {alertText.title}
            </p>

            <p className="text-xs italic font-light">
              {alertText.description}
            </p>
          </div>
        </Alert>
      </div>

      <SelectController
        control={control}
        name="equipment_category_specifications_attributes"
        options={equipmentCategoriesOptions}
        onInputChange={setSearchEquipmentCategories}
        label="Categorias de equipamentos"
        isMulti
        operationKlassToCheckPermission="Admin::EquipmentCategories::Filter"
        errorMessage={errors.equipment_category_specifications_attributes?.message || ''}
        markAsRequired
      />

      <SelectController
        control={control}
        name="equipment_manufacturer_specifications_attributes"
        options={equipmentManufacturersOptions}
        onInputChange={setSearchEquipmentManufacturers}
        label="Fabricantes"
        isMulti
        operationKlassToCheckPermission="Admin::EquipmentManufacturers::Filter"
        errorMessage={errors.equipment_manufacturer_specifications_attributes?.message || ''}
        markAsRequired
      />

      <SelectController
        control={control}
        name="system_specifications_attributes"
        options={objetiveSystemsOptions}
        onInputChange={setSearchObjectiveSystems}
        label="Sistemas de manutenção atendidos"
        isMulti
        operationKlassToCheckPermission="Admin::Maintenance::ObjectiveSystems::Filter"
        errorMessage={errors.system_specifications_attributes?.message || ''}
        markAsRequired
      />
    </div>
  );
}

export default MaintenanceSpecificationForm;
